import { ChevronsUpDown, Plus } from 'lucide-react'
import { useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from '@/lib/toast'
import { createCategory, listCategories } from '../settings/api'
import type { CategoryScope, CrmCategory } from '../settings/types'
import type { CrmCategoryRef } from '../types'
import { CategoryChip } from './CategoryChip'

/**
 * The list of a popover that opens from inside a modal (the "Nieuwe organisatie" sheet):
 * the modal's scroll lock cancels wheel and touch events from outside its own DOM, and the
 * popover is portaled to <body>. Stopping them here keeps the list scrollable.
 */
const keepScrolling = (event: { stopPropagation: () => void }) => event.stopPropagation()

/**
 * Pick one category of a scope, or "Geen categorie"; a name that does not exist yet
 * can be created on the spot (design §4.8: new value gets the next colour, icon tag).
 */
export function CategorySelect({
  value,
  onChange,
  label = 'Categorie',
  scope = 'relatie',
}: {
  value: CrmCategoryRef | null
  onChange: (category: CrmCategory | null) => void
  label?: string
  scope?: CategoryScope
}) {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<CrmCategory[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [query, setQuery] = useState('')
  const creating = useRef(false)
  const trimmed = query.trim()
  const needle = trimmed.toLowerCase()
  const exact = options?.some((option) => option.name.toLowerCase() === needle)
  // Names that contain the text, those that start with it first. cmdk does not filter
  // (`shouldFilter={false}`): its own scoring ranked "create" above a match for text from
  // the middle of a name, and hid the "create" row, with its group, when nothing matched.
  const starts = (name: string) => name.toLowerCase().startsWith(needle)
  const visible = (options ?? [])
    .filter((option) => option.name.toLowerCase().includes(needle))
    .sort((a, b) => Number(starts(b.name)) - Number(starts(a.name)))

  function load() {
    setLoadFailed(false)
    listCategories(scope)
      .then(setOptions)
      .catch((caught) => {
        // The list stays unknown (not empty): opening the picker again tries again, and
        // no typed name is offered as new while the existing names are not known.
        setLoadFailed(true)
        toast.error(caught instanceof Error ? caught.message : 'Categorieën laden mislukt.')
      })
  }

  function changeOpen(next: boolean) {
    setOpen(next)
    if (next && options === null) load()
  }

  function choose(category: CrmCategory | null) {
    onChange(category)
    setOpen(false)
    setQuery('')
  }

  async function create(name: string) {
    // A second activation while the first is on its way would post the same name twice.
    if (creating.current) return
    creating.current = true
    try {
      const created = await createCategory({ scope, name })
      setOptions((current) => [...(current ?? []), created])
      choose(created)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Categorie aanmaken mislukt.')
    } finally {
      creating.current = false
    }
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className="flex min-h-8 w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-2 py-1 text-left text-sm"
        >
          {value ? (
            <CategoryChip category={value} />
          ) : (
            <span className="text-muted-foreground">Geen categorie</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Zoek of maak een categorie…"
          />
          <CommandList onWheel={keepScrolling} onTouchMove={keepScrolling}>
            {options === null ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {loadFailed ? 'Kon de categorieën niet laden.' : 'Laden…'}
              </p>
            ) : (
              <CommandEmpty>Geen categorieën gevonden.</CommandEmpty>
            )}
            <CommandGroup>
              {trimmed ? null : (
                <CommandItem value="__geen__" onSelect={() => choose(null)}>
                  Geen categorie
                </CommandItem>
              )}
              {visible.map((option) => (
                <CommandItem key={option.id} value={option.id} onSelect={() => choose(option)}>
                  <CategoryChip category={option} />
                </CommandItem>
              ))}
              {trimmed && options !== null && !exact ? (
                <CommandItem value="__nieuw__" onSelect={() => void create(trimmed)}>
                  <Plus className="size-4" aria-hidden />
                  Nieuwe categorie &lsquo;{trimmed}&rsquo; aanmaken
                </CommandItem>
              ) : null}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
